import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCountry } from "@/lib/country";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BrandLogo } from "@/components/Brand";
import { Heart, Home, Activity, Stethoscope, Library, Map, Users, Contact, MessageCircle, MessageSquareHeart, FileSignature, LifeBuoy, ClipboardList, LogOut, ShieldCheck, BookOpen, MessageSquareQuote, Sparkles, FlaskConical, Wallet, BookHeart, FileText, Building2, FolderOpen, Compass, ChevronDown, SlidersHorizontal } from "lucide-react";
import TourWizard from "@/components/TourWizard";
import FinishStudyButton from "@/components/FinishStudyButton";
import TesterTaskPanel from "@/components/TesterTaskPanel";
import TesterLockoutGate from "@/components/TesterLockoutGate";
import TrialAccessGate, { useTrackSectionVisits, useHeartbeat } from "@/components/TrialAccessGate";
import SurveyNudge from "@/components/SurveyNudge";
import OrgConsentPrompt from "@/components/OrgConsentPrompt";
import CountryLanguageSelector from "@/components/CountryLanguageSelector";
import PartnerBrandBar from "@/components/PartnerBrandBar";
import MobileBottomNav from "@/components/MobileBottomNav";
import { getParticipantId } from "@/lib/userInterviews";
import { useFirstVisitMode } from "@/lib/firstVisit";
import { SECTION_COLORS } from "@/lib/sectionColors";

const NAV_ITEMS = [
    // "Rooms in a peaceful home" IA (Jul 2026): Home = comfort · Roadmap =
    // where am I · Ask Lane = help me now · Learn = teach me · Resources =
    // connect me · GUIDE = Medicare GUIDE · Wellness = you matter too ·
    // Care Summary = everything remembered, ready to share.
    // match[] = every route that should light this tab up.
    // colorKey ties each tab to its section accent (lib/sectionColors.js).
    { to: "/dashboard", key: "nav.home", icon: Home, testid: "nav-home", match: ["/dashboard"], colorKey: "home" },
    { to: "/roadmap", key: "nav.roadmap", icon: Map, testid: "nav-roadmap", match: ["/roadmap", "/care-history"], colorKey: "roadmap" },
    { to: "/chat", key: "nav.askLane", icon: MessageCircle, testid: "nav-talk-to-lane", match: ["/chat"], colorKey: "askLane" },
    { to: "/education", key: "nav.learn", icon: BookOpen, testid: "nav-education", match: ["/education", "/treatments", "/guidance", "/conversations", "/who-pays"], colorKey: "learn" },
    { to: "/resources-hub", key: "nav.resources", icon: Compass, testid: "nav-resources", match: ["/resources-hub", "/help-finder"], colorKey: "resources" },
    { to: "/guide", key: "nav.guide", icon: Stethoscope, testid: "nav-guide", usOnly: true, match: ["/guide"], colorKey: "guide" },
    { to: "/wellness", key: "nav.wellness", icon: Heart, testid: "nav-wellness", match: ["/wellness", "/burnout"], colorKey: "wellness" },
    { to: "/care-summary", key: "nav.careSummary", icon: FileText, testid: "nav-care-summary", match: ["/care-summary", "/care-team-summary", "/appointment"], colorKey: "careSummary" },
];

// More menu: everything else stays reachable — nothing was deleted.
const MORE_ITEMS = [
    { to: "/journey", key: "nav.journey", icon: BookHeart, testid: "nav-journey" },
    { to: "/care-team", key: "nav.careTeamMain", icon: Contact, testid: "nav-care-team-tab" },
    { to: "/settings", key: "nav.settings", icon: SlidersHorizontal, testid: "nav-settings" },
    { to: "/feedback", key: "nav.feedback", icon: MessageSquareQuote, testid: "nav-feedback" },
    { to: "/support", key: "nav.help", icon: LifeBuoy, testid: "nav-help" },
    { to: "/privacy", key: "nav.privacy", icon: ShieldCheck, testid: "nav-privacy" },
];

export default function Layout() {
    const { user, logout, refresh } = useAuth();
    const { t, country } = useCountry();
    const location = useLocation();

    // Admins always see the full toolset, including US-gated items, since
    // Ashlee navigates US families regardless of her own profile country.
    const isUS = user?.role === "admin" || (country || "US").toUpperCase() === "US";
    const visibleNavItems = React.useMemo(
        () => NAV_ITEMS.filter(it => !it.usOnly || isUS),
        [isUS]
    );

    // Tester 30-minute timer starts here, not at link-click. Any tester who
    // reaches a Layout-wrapped route (dashboard, chat, etc.) is treated as
    // having "actually started" the study — POST /me/study-start is
    // idempotent so repeat mounts are no-ops. This way someone who just
    // clicks the Prolific link to peek and bounces won't get locked out
    // before they ever sit down to use it.
    const studyStartCalled = useRef(false);
    useEffect(() => {
        if (!user || user === false) return;
        if (user.role === "admin") return;
        if (!user.tester_code) return;
        if (user.study_started_at) return;
        if (studyStartCalled.current) return;
        studyStartCalled.current = true;
        (async () => {
            try {
                await api.post("/me/study-start");
                await refresh();
            } catch (_e) { /* no-op — backend will just return 200 */ }
        })();
    }, [user, refresh]);

    // Section-visit tracking — pings the backend whenever the user lands on a
    // tracked route. Powers the trial gate (for non-tester users) and the
    // Tester at-a-glance panel in admin (for Prolific testers).
    useTrackSectionVisits(api);

    // Heartbeat — increments cumulative active time on the server while the
    // tab is foregrounded and the user has interacted recently. Drives the
    // 15-minute trial gate and the admin "Active now" widget.
    useHeartbeat(api);

    // Show the take-the-tour link only for approved (non-admin) users.
    const canSeeTour = user && user !== false && user.status === "approved" && user.role !== "admin";

    // If this caregiver landed via /tester/:code?iid=… we keep the Finish-study
    // CTA in the header for the whole session so they can complete the User
    // Interviews task whenever they're done — not just from the Feedback page.
    // Strictly tester-only: requires both an iid in storage AND a tester
    // account (has tester_code), so regular approved users / admins never see
    // it even if they once tested via /tester/?iid=… on the same device.
    const participantId = getParticipantId();
    const firstVisit = useFirstVisitMode();
    const isTesterUser = !!(user && user !== false && user.role !== "admin" && user.tester_code);
    const canFinishStudy = !!participantId && isTesterUser;

    const replayTour = async () => {
        try {
            await api.post("/auth/tour-state", { state: "later" });
            await refresh();
            toast.success("Tour is ready — head to your Dashboard.");
        } catch {
            toast.error("Couldn't reset the tour. Try again.");
        }
    };

    return (
        <div className="min-h-screen bg-cream flex flex-col pb-[72px] md:pb-0">
            <header className="bg-white/80 backdrop-blur-sm border-b border-[#E2E8F0] sticky top-0 z-30 no-print">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4">
                    <Link to="/dashboard" className="flex items-center shrink-0" data-testid="brand-link">
                        <BrandLogo className="h-6 sm:h-7" />
                    </Link>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <CountryLanguageSelector variant="compact" />
                        {user?.role === "admin" && (
                            <Link to="/admin" data-testid="layout-admin-link"
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-dark bg-teal-soft hover:bg-teal/20 rounded-2xl px-3 py-2 min-h-[44px]">
                                <ShieldCheck size={16} strokeWidth={1.6} /> Admin
                            </Link>
                        )}
                        <OrgAdminHeaderLink user={user} />
                        {!firstVisit && (
                        <Link
                            to="/chat"
                            data-testid="header-chat-button"
                            className="inline-flex items-center justify-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-4 py-2 font-medium transition-all min-h-[44px] text-sm md:text-base"
                        >
                            <MessageCircle size={18} strokeWidth={1.8} />
                            <span className="hidden sm:inline">{t("common.talkToLane")}</span>
                            <span className="sm:hidden">Lane</span>
                        </Link>
                        )}
                        {!firstVisit && canSeeTour && (
                            <button
                                onClick={replayTour}
                                data-testid="layout-take-tour"
                                title={t("common.takeTheTour")}
                                className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#4C5551] hover:text-teal-dark px-3 py-2"
                            >
                                <Sparkles size={14} strokeWidth={1.7} />
                                <span className="hidden md:inline">{t("common.takeTheTour")}</span>
                            </button>
                        )}
                        {canFinishStudy && (
                            <FinishStudyButton iid={participantId} />
                        )}
                        <button onClick={logout} data-testid="layout-logout"
                            className="text-sm font-medium text-[#4C5551] hover:text-teal-dark inline-flex items-center justify-center gap-1 px-3 py-2 min-h-[44px] min-w-[44px]">
                            <LogOut size={16} strokeWidth={1.6} />
                            <span className="hidden sm:inline">{t("common.signOut")}</span>
                        </button>
                    </div>
                </div>
                {/* Eight-room caregiver nav (desktop) — on mobile this wrapped
                    into a 200px sticky header, so < md it's replaced by the
                    fixed bottom nav (MobileBottomNav). Collapsed to a minimal
                    header on a brand-new user's first home screen. */}
                {!firstVisit && (
                <nav className="hidden md:block max-w-6xl mx-auto px-2 sm:px-4 pb-2">
                    <ul className="flex gap-1 flex-wrap items-center">
                        {visibleNavItems.map((it) => {
                            const isActive = (it.match || [it.to]).some(
                                m => location.pathname === m || location.pathname.startsWith(m + "/")
                            );
                            const c = SECTION_COLORS[it.colorKey] || SECTION_COLORS.home;
                            return (
                                <li key={it.to}>
                                    <NavLink
                                        to={it.to}
                                        data-testid={it.testid}
                                        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "bg-[#4F7F95] text-white shadow-sm" : "text-[#4C5551] hover:bg-[#EDF3F6] hover:text-[#3A6379]"}`}
                                    >
                                        <it.icon size={16} strokeWidth={1.8} style={{ color: isActive ? "#FFFFFF" : "#4F7F95" }} />
                                        {t(it.key)}
                                    </NavLink>
                                </li>
                            );
                        })}
                        <li><NavMore items={MORE_ITEMS} t={t} /></li>
                    </ul>
                </nav>
                )}
            </header>

            <PartnerBrandBar user={user} />

            <main className="flex-1">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                    <Outlet />
                </div>
            </main>

            {!firstVisit && <TourWizard key={user && user !== false ? `${user.id}-${user.tour_state || "fresh"}` : "anon"} />}
            <TesterTaskPanel />
            <TesterLockoutGate />
            <TrialAccessGate />
            <SurveyNudge />
            <OrgConsentPrompt />
            {/* NeedHelpTodayFAB removed from authed routes (founder, Jun 2026):
                the pill overlapped content on phones. The dashboard banner and
                footer crisis lines keep the numbers one tap away. */}
            <MobileBottomNav t={t} isUS={isUS} />

            <footer className="bg-cream-dark border-t border-[#E2E8F0] no-print" data-testid="disclaimer-banner">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-xs sm:text-sm text-[#4C5551] leading-relaxed">
                    <strong className="text-[#1F2624]">A gentle reminder:</strong> Memory Lane offers educational and
                    organizational support only. It is not medical advice, diagnosis, treatment, emergency care, or legal
                    advice. In an emergency call <a className="text-teal underline inline-block py-2" href="tel:911">911</a>. For mental
                    health crisis, call or text <a className="text-teal underline inline-block py-2" href="tel:988">988</a>. For dementia
                    support, call the Alzheimer&apos;s Association 24/7 Helpline at{" "}
                    <a className="text-teal underline inline-block py-2" href="tel:18002723900">1-800-272-3900</a>.
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs items-center">
                        <Link to="/terms" data-testid="footer-terms" className="text-[#4C5551] hover:text-teal-dark underline inline-block py-2">Terms of Service</Link>
                        <Link to="/privacy" data-testid="footer-privacy" className="text-[#4C5551] hover:text-teal-dark underline inline-block py-2">Privacy Policy</Link>
                        <span className="text-[#6E7A74]">© {new Date().getFullYear()} Memory Lane Care Navigator</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}

/* Org-admin header pill — surfaces only for users who are admin of at least
   one organization (queried lazily on mount). Platform admins already have
   their own /admin link; this is for partner org_admins (hospice / GUIDE
   programs / memory clinics). */
function OrgAdminHeaderLink({ user }) {
    const [showing, setShowing] = React.useState(false);
    React.useEffect(() => {
        let active = true;
        if (!user || user === false) return undefined;
        // Platform admins reach OrgAdmin from /org-admin already; we still
        // surface it here so they have one-click access.
        if (user.role === "admin") {
            setShowing(true);
            return undefined;
        }
        (async () => {
            try {
                const r = await api.get("/org/me");
                if (!active) return;
                setShowing((r.data?.is_org_admin_of || []).length > 0);
            } catch {
                /* ignore — not an org admin */
            }
        })();
        return () => { active = false; };
    }, [user]);
    if (!showing) return null;
    return (
        <Link
            to="/org-admin"
            data-testid="layout-org-admin-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-stage_late-dark bg-stage_late/30 hover:bg-stage_late/40 rounded-2xl px-3 py-2 min-h-[44px]"
        >
            <Building2 size={16} strokeWidth={1.6} /> Org
        </Link>
    );
}


/** Overflow nav — keeps the bar one row with no clipped labels. */
function NavMore({ items, t }) {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const ref = useRef(null);

    useEffect(() => { setOpen(false); }, [location.pathname]);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    if (!items.length) return null;
    const activeInside = items.some(it => location.pathname.startsWith(it.to));

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                data-testid="nav-more"
                className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-2xl text-sm font-medium whitespace-nowrap transition-colors ${activeInside ? "bg-teal text-white" : "text-[#4C5551] hover:bg-cream-dark"}`}
            >
                More <ChevronDown size={14} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    role="menu"
                    data-testid="nav-more-menu"
                    className="absolute left-0 top-full mt-1.5 z-40 w-60 rounded-2xl border border-cream-dark bg-white shadow-lg p-1.5 max-h-[70vh] overflow-y-auto"
                >
                    {items.map(it => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            data-testid={it.testid}
                            role="menuitem"
                            className={({ isActive }) =>
                                `flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-teal text-white" : "text-[#4C5551] hover:bg-cream-light"}`
                            }
                        >
                            <it.icon size={16} strokeWidth={1.6} />
                            {t(it.key)}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}
